import PropTypes from "prop-types";
import { useState, useContext } from "react";

import { useTracking } from "@context/Tracking";
import { CartContext } from "@context/CartProvider";
import { useAddToCartWithLuxe } from "@context/LuxeProvider";

import { useCurrentCountryCode, useCurrentCurrencyCode } from "@hooks/usePrices";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { useDebounce } from "@hooks/useDebounce";

import { prepareAddToCart } from "@helpers/product";
import { useALError } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { applyIgPrices } from "@helpers/intelligems";

import { ALButtonV2 } from "@components/ALComponents";

import giftNotePreview from "@images/gift-note-preview.png";
import CheckIcon from "@svgs/checkbox-filled.svg";

const PREFILLED_NOTE =
  "Happy birthday, Jenny! I hope you love this piece as much as I loved picking it out for you.\n\nLove,\n Sarah";
const MAX_LENGTH = 300;

function GiftNote({ closePopup, selectedPackaging, cartNote, packageInCart, checkoutUrl }) {
  const { updateCartNote, removeSingleProductsFromCart } = useContext(CartContext);

  const [note, setNote] = useState(cartNote || "");
  const [sendBlankCard, setSendBlankCard] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const countryCode = useCurrentCountryCode();
  const currencyCode = useCurrentCurrencyCode();
  const discountsInfo = useDiscountsInfo();
  const { sendReport } = useALError();
  const addToCartWithLuxe = useAddToCartWithLuxe();
  const { trackGiftPackagingActions } = useTracking();

  const debouncedNote = useDebounce(note, 300);
  const previewText = sendBlankCard ? "" : debouncedNote || cartNote || PREFILLED_NOTE;

  const igPricesForProduct = applyIgPrices(
    selectedPackaging?.id,
    selectedPackaging?.variants?.[0],
    selectedPackaging
  );

  const handleAddToCart = async ({ action }) => {
    setIsAddingToCart(true);
    try {
      // Only add the selected packaging to the cart if it is different from the one already in the cart
      if (packageInCart?.variant.product.handle !== selectedPackaging.handle) {
        const { dataProductsToAdd } = prepareAddToCart({
          product: selectedPackaging,
          selectedVariant: selectedPackaging.variants[0],
          productPrices: igPricesForProduct,
          countryCode,
          currencyCode,
          discountsInfo,
        });

        if (dataProductsToAdd.length === 0) {
          throw new Error("No packaging product prepared for the cart");
        }

        const addedCart = await addToCartWithLuxe({ dataProductsToAdd });
        if (!addedCart) {
          throw new Error("Failed to add gift packaging");
        }

        if (packageInCart) {
          const updatedCart = await removeSingleProductsFromCart({
            lineToRemove: packageInCart,
          });
          if (!updatedCart) {
            throw new Error("Failed to remove previous gift packaging");
          }
        }
      }
      // Only update the cart note if it has changed
      if (cartNote !== note) {
        const updatedCart = await updateCartNote(note);
        if (!updatedCart) {
          throw new Error("Failed to update gift note");
        }
      }

      trackGiftPackagingActions({
        action: action === "checkout" ? "checkout_button_click" : "review_button_click",
        productId: selectedPackaging?.id,
      });

      if (action === "checkout") {
        window.location.href = checkoutUrl;
      }
      closePopup();
    } catch (error) {
      sendReport(error, { name: "GiftNote", priority: "P1" });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBlankCardClick = () => {
    setSendBlankCard(!sendBlankCard);
    if (!sendBlankCard) {
      setNote("");
    } else {
      setNote(cartNote);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <p className="typo-h2-bold">Personalize Your Gift Note</p>
      <p className="typo-body mt-[5px]">
        We&apos;ll print it on an Ana Luisa card and tuck it into the box.
      </p>
      <div className="desktop:flex desktop:mt-[20px] desktop:gap-[20px]">
        <div className="relative mobile:mt-[15px] mb-[20px] desktop:mb-0 desktop:w-[375px]">
          <img src={giftNotePreview} alt="Gift Note Preview" className="w-full" />
          <span className="absolute typo-nano whitespace-pre-line break-words top-[30%] inset-x-[20%] text-left">
            {previewText}
          </span>
        </div>

        <div className="desktop:flex desktop:flex-col flex-1">
          <div className="relative typo-body mobile:text-[16px] desktop:w-full flex-1">
            <textarea
              id="gift-note-message"
              maxLength={MAX_LENGTH}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full desktop:w-[375px] h-[165px] desktop:h-full p-[15px] pb-[25px] desktop:p-[20px] rounded-[4px] bg-light-grey resize-none"
              placeholder="Enter your note here"
              disabled={sendBlankCard}
            />
            <p className="absolute bottom-[15px] desktop:bottom-[20px] right-[15px] desktop:right-[20px] text-dark-grey">
              {note.length}/{MAX_LENGTH}
            </p>
          </div>
          <label
            htmlFor="blank-card"
            className="typo-body flex items-center mt-[15px] cursor-pointer"
          >
            <span className="relative mr-[10px] w-[20px] h-[20px] shrink-0">
              <input
                id="blank-card"
                type="checkbox"
                name="blank-card"
                className="sr-only"
                checked={sendBlankCard}
                onChange={handleBlankCardClick}
              />
              {sendBlankCard ? (
                <CheckIcon className="w-full h-full" />
              ) : (
                <span className="block w-full h-full border border-black rounded-[2px]" />
              )}
            </span>
            Send a blank card instead
          </label>
        </div>
      </div>

      <div className="mt-auto desktop:flex desktop:flex-row-reverse desktop:gap-[20px]">
        <ALButtonV2
          fullWidth
          size="large"
          onClick={() => handleAddToCart({ action: "checkout" })}
          className="mt-[35px] desktop:mt-[50px]"
          disabled={isAddingToCart}
          isLoading={isAddingToCart}
        >
          Add & Go to Checkout
        </ALButtonV2>
        <ALButtonV2
          fullWidth
          size="large"
          variant="outline"
          onClick={handleAddToCart}
          className="mt-[10px] desktop:mt-[50px]"
          disabled={isAddingToCart}
          isLoading={isAddingToCart}
        >
          Add & Review Cart
        </ALButtonV2>
      </div>
    </div>
  );
}

GiftNote.propTypes = {
  closePopup: PropTypes.func.isRequired,
  selectedPackaging: PropTypes.objectOf(
    PropTypes.shape({
      handle: PropTypes.string.isRequired,
    })
  ).isRequired,
  cartNote: PropTypes.string,
  packageInCart: PropTypes.objectOf(
    PropTypes.shape({
      variant: PropTypes.shape({
        product: PropTypes.shape({
          handle: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    })
  ).isRequired,
  checkoutUrl: PropTypes.string.isRequired,
};

export default GiftNote;
